import { useCallback } from 'react';
import uuid from 'uuid/v4';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useContentType, usePageContentId } from '@confluence/page-context';

import { HighlightKeywordsQuery } from './gql/HighlightKeywordsQuery.agggraphql.ts';
import { generatePageARI, generateWorkspaceId } from './reading-aids-utils.ts';
import type {
	HighlightKeywordsQuery$data,
	HighlightKeywordsQuery$variables,
	KnowledgeDiscoveryKeyPhraseCategory,
} from './gql/__generated__/HighlightKeywordsQuery.graphql.ts';

type HighlightPhraseNodeType = {
	phrase: string;
	category: KnowledgeDiscoveryKeyPhraseCategory;
};

type QueryHighlightAcronymsType = {
	acronymRegex?: RegExp;
	acronyms: HighlightPhraseNodeType[];
};

const cachedAcronymRegex = {
	pageARI: '',
	regex: new RegExp('(?!)'),
};

const getAcronymRegex = (
	acronyms: HighlightPhraseNodeType[],
	pageARI: string = '',
	ignoreCase: boolean = false,
): RegExp => {
	if (pageARI === cachedAcronymRegex.pageARI) {
		return cachedAcronymRegex.regex;
	}
	let regex;
	try {
		regex = new RegExp(
			acronyms.map((node) => `\\b${node.phrase}\\b`).join('|'),
			ignoreCase ? 'gi' : 'g',
		);
	} catch {
		regex = new RegExp('(?!)');
	}
	cachedAcronymRegex.pageARI = pageARI;
	cachedAcronymRegex.regex = regex;
	return regex;
};

export const useQueryHighlightAcronyms = (): {
	queryHighlightAcronyms: ({
		cacheKey,
	}?: {
		cacheKey?: string;
	}) => Promise<QueryHighlightAcronymsType>;
} => {
	const [contentId] = usePageContentId();
	const [contentType] = useContentType();
	const { cloudId, activationId } = useSessionData();
	const pageARI = contentId ? generatePageARI(contentId, cloudId, contentType) : undefined;
	const workspaceId = generateWorkspaceId(activationId, cloudId);

	const queryHighlightAcronyms = useCallback(
		async ({
			cacheKey = uuid(),
		}: { cacheKey?: string } = {}): Promise<QueryHighlightAcronymsType> => {
			if (!workspaceId || !pageARI) {
				return {
					acronyms: [],
				};
			}

			try {
				const { data } = await queryInterop<
					HighlightKeywordsQuery$data,
					HighlightKeywordsQuery$variables
				>({
					query: HighlightKeywordsQuery,
					fetchPolicy: 'network-only',
					errorPolicy: 'ignore',
					variables: {
						// comment from confluence/next/packages/content-appearance/src/contentAppearance.ts
						// This is dummy variable is used to force "network-only" requests to only be run as network calls.
						// A bug in Apollo sometimes makes this query return data from the cache even if the policy is "network-only"
						// See for more details: https://pug.jira-dev.com/wiki/spaces/~683304838/pages/5971970200/Apollo+Client+-+weird+refetch+behavior
						// @ts-expect-error
						cacheKey,
						pageARI,
						workspaceId,
					},
					interopApolloAggClient: true,
				});

				const keyPhrasesData = data?.knowledgeDiscovery?.keyPhrases;
				const keyPhrases =
					keyPhrasesData &&
					keyPhrasesData.__typename === 'KnowledgeDiscoveryKeyPhraseConnection' &&
					keyPhrasesData.nodes
						? // eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
							keyPhrasesData.nodes
								.filter((phrase): phrase is NonNullable<typeof phrase> => phrase != null)
								.map((phrase) => ({
									phrase: phrase.keyPhrase,
									category: phrase.category,
								}))
						: [];

				// getAcronymRegex use pageARI to cache the regex for the page
				// but for live edit pages we should be careful with the cache
				// because the page content is changing constantly
				const pageARIWithCacheKey = `${pageARI}-${cacheKey}`;
				let acronymRegex: RegExp | undefined = undefined;
				if (keyPhrases.length) {
					acronymRegex = getAcronymRegex(keyPhrases, pageARIWithCacheKey, true);
				}

				return {
					acronyms: keyPhrases,
					acronymRegex,
				};
			} catch {
				return {
					acronyms: [],
				};
			}
		},
		[pageARI, workspaceId],
	);

	return { queryHighlightAcronyms };
};
